import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  useMassagerGetUser,
  useMassagerLogin,
  useMassagerLogout,
  useMassagerRefreshToken,
} from '@/hooks/massager/useMassagerAuth';
import type {
  MassagerAuthContextType,
  MassagerAuthState,
  MassagerLoginCredentials,
  MassagerLoginResult,
} from '@/types/massagerAuth';
import { getRouter } from '@/shared/lib/routerRef';

const TOKEN_KEY = 'massager_token';
const MASSAGER_KEY = 'massager_user';
const CLINIC_KEY = 'massager_clinic';
const SITE_KEY = 'massager_site';

const deriveSiteFromLocation = (): string => {
  const match = window.location.pathname.match(/^\/([^/]+)\/massager(?:\/|$)/);
  return match?.[1] || localStorage.getItem(SITE_KEY) || 'medix';
};

const initialState: MassagerAuthState = {
  massager: null,
  clinic: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const MassagerAuthContext = createContext<MassagerAuthContextType | undefined>(undefined);

interface MassagerAuthProviderProps {
  children: React.ReactNode;
}

export const MassagerAuthProvider: React.FC<MassagerAuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<MassagerAuthState>(initialState);

  const loginMutation = useMassagerLogin();
  const logoutMutation = useMassagerLogout();
  const refreshTokenMutation = useMassagerRefreshToken();

  const derivedSite = deriveSiteFromLocation();
  const getUserQuery = useMassagerGetUser(derivedSite);

  const [storedAuth, setStoredAuth] = useState(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const massagerRaw = localStorage.getItem(MASSAGER_KEY);
    const clinicRaw = localStorage.getItem(CLINIC_KEY);

    return {
      token,
      massager: massagerRaw ? JSON.parse(massagerRaw) : null,
      clinic: clinicRaw ? JSON.parse(clinicRaw) : null,
    };
  });

  useEffect(() => {
    if (!storedAuth.token || !storedAuth.massager) {
      setState({ ...initialState, isLoading: false });
      return;
    }

    if (getUserQuery.isSuccess) {
      setState({
        massager: getUserQuery.data.massager,
        clinic: getUserQuery.data.clinic,
        token: storedAuth.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      return;
    }

    if (getUserQuery.isError) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(MASSAGER_KEY);
      localStorage.removeItem(CLINIC_KEY);
      localStorage.removeItem(SITE_KEY);
      setState({ ...initialState, isLoading: false });
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));
  }, [
    getUserQuery.isSuccess,
    getUserQuery.isError,
    getUserQuery.data,
    storedAuth.token,
    storedAuth.massager,
  ]);

  const login = async (
    credentials: MassagerLoginCredentials
  ): Promise<MassagerLoginResult> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await loginMutation.mutateAsync(credentials);

      if (response.success) {
        setStoredAuth({
          token: response.data.token,
          massager: response.data.massager,
          clinic: response.data.clinic,
        });
        setState({
          massager: response.data.massager,
          clinic: response.data.clinic,
          token: response.data.token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        return { success: true };
      }

      const failMessage = response.message || 'ログインに失敗しました';
      setState(prev => ({ ...prev, isLoading: false, error: failMessage }));
      return { success: false, message: failMessage };
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          data?: { message?: string; errors?: { password?: string[] } };
        };
      };
      const data = axiosError?.response?.data;
      const message =
        data?.errors?.password?.[0] ||
        data?.message ||
        (error instanceof Error ? error.message : null) ||
        'ログインに失敗しました';
      setState(prev => ({ ...prev, isLoading: false, error: message }));
      return { success: false, message };
    }
  };

  const logout = async (): Promise<void> => {
    const site =
      localStorage.getItem(SITE_KEY) || state.clinic?.site_type || derivedSite;
    const loginSite = site.toLowerCase().includes('upsystem') ? 'medix' : site;

    // Suppress the "token expired" toast that the axios 401-interceptor would
    // otherwise display after a manual logout invalidates the token.
    sessionStorage.setItem('suppress_force_logout_toast_once', '1');

    try {
      await logoutMutation.mutateAsync({ site });
    } catch {
      // Logout errors are intentionally ignored — local cleanup runs in `finally`.
    } finally {
      // Redirect BEFORE clearing auth state — ProtectedMassagerRoute re-renders on
      // state clear and would fall back to the wrong site's login path otherwise.
      await getRouter().navigate(`/${loginSite}/massager/login`, { replace: true });

      setStoredAuth({ token: null, massager: null, clinic: null });
      setState({ ...initialState, isLoading: false });
    }
  };

  const refreshToken = async (): Promise<void> => {
    const site = localStorage.getItem(SITE_KEY) || derivedSite;
    try {
      const response = await refreshTokenMutation.mutateAsync({ site });
      if (response.success && response.data?.token) {
        setState(prev => ({ ...prev, token: response.data!.token }));
      } else {
        await logout();
      }
    } catch {
      await logout();
    }
  };

  const value: MassagerAuthContextType = {
    ...state,
    login,
    logout,
    refreshToken,
  };

  return (
    <MassagerAuthContext.Provider value={value}>{children}</MassagerAuthContext.Provider>
  );
};

/**
 * Hook to access the massager auth context.
 * Falls back to a storage-backed read when the provider is not mounted (e.g., during HMR)
 * so guarded routes don't flicker.
 */
export const useMassagerAuthContext = (): MassagerAuthContextType => {
  const context = useContext(MassagerAuthContext);

  if (context === undefined) {
    const storedMassager = localStorage.getItem(MASSAGER_KEY);
    const storedClinic = localStorage.getItem(CLINIC_KEY);
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const massager = storedMassager ? JSON.parse(storedMassager) : null;
    const clinic = storedClinic ? JSON.parse(storedClinic) : null;

    return {
      massager,
      clinic,
      token: storedToken,
      isAuthenticated: !!storedToken && !!massager,
      isLoading: false,
      error: null,
      login: async () => ({ success: true }),
      logout: async () => { },
      refreshToken: async () => { },
    };
  }

  return context;
};

export default MassagerAuthContext;
